import { safeIrr } from "./irr";
import { SeriesByYear, YearKey } from "./series";
import { Deal } from "@/types/deal";
import { ExitStrategy } from "@/types/exitStrategy";

export type CashFlowRow = {
  id: string;
  label: string;
  section: "memo" | "unlevered" | "levered";
  type: "line" | "subtotal" | "total" | "section";
  years: Array<{ year: number; value: number }>;
};

export type DebtScheduleRow = {
  id: string;
  label: string;
  type: "debt" | "balance";
  years: Array<{ year: number; value: number }>;
};

export type DebtSchedule = {
  rows: DebtScheduleRow[];
  totalLoanAmount: number;
  totalInterest: number;
  totalPrincipalRepaid: number;
};

export interface DebtScheduleRowData {
  year: number;
  beginningBalance: number;
  payment: number;
  interest: number;
  principal: number;
  endingBalance: number;
}

export type CashFlowKPIs = {
  unlevered10y: number;
  levered10y: number;
  tax10y: number;
  avgEbitdaMargin: number;
  unleveredIRR: number | null;
  leveredIRR: number | null;
  totalLoanAmount?: number;
  totalInterest?: number;
};

export type CashFlowResult = {
  rows: CashFlowRow[];
  debtScheduleRows: DebtScheduleRowData[];
  kpis: CashFlowKPIs;
  exitYear: number | null;
  debtSchedule: DebtSchedule;
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function getStandardYears(): YearKey[] {
  return Array.from({ length: 11 }, (_, i) => `y${i}` as YearKey);
}

function resolveExitYear(deal: Deal): number | null {
  const e = deal.kpis?.exit;
  if (!e) return null;
  if (e.strategy === "SALE")
    return e.sale?.exitYear ?? e.holdPeriodYears ?? null;
  if (e.strategy === "REFINANCE")
    return e.refinance?.refinanceYear ?? e.holdPeriodYears ?? null;
  return e.holdPeriodYears ?? null;
}

// ─── P&L re-implementation по формулам Excel ─────────────────────────────────
//
// Все формулы — прямой перевод из листа PL:
//
// toplineIndex[n] = (1+toplineGrowthPct)^n          PL!row76
// inflationIndex[n] = (1+inflationPct)^n             PL!row77
// revenueRamp[n] — из Ramp&Macro                     PL!row79
// costRamp[n]    — из Ramp&Macro                     PL!row80
// assetOpen[n]   = 1 если n <= exitYear              PL!row66
//
// ADR[n]  = baseADR * toplineIndex * revenueRamp * assetOpen     PL!row69
// Occ[n]  = baseOcc * revenueRamp * assetOpen                    PL!row70
// RoomsAvail[n] = totalRooms * 365 * assetOpen                   PL!row73
// RoomsSold[n]  = Occ * RoomsAvail                               PL!row74
// RoomsRev[n]   = ADR * RoomsSold                                PL!row6
//
// F&B Revenue = sum(Breakfast, Lunch, Dinner, Bar)
//   Each = TotalGuests*capture*checkGuest + extCustPerDay*365*checkExt   PL!row91,97,103,109
//
// SpaRevenue  = treatmentsPerDay * 365 * avgPrice * assetOpen            PL!row113
// OtherRevenue = pctOfRooms * RoomsRev + fixedMonthly * 12 * assetOpen  PL!row117
//
// COSTS (умножение на costRamp и inflationIndex — только payroll и fixed costs):
//   RoomsPayroll   = annual * costRamp * inflationIndex * assetOpen       PL!row12
//   Commission     = pct * costRamp * RoomsRev  (нет inflIdx)            PL!row13
//   GuestSupplies  = perNight * costRamp * inflIdx * assetOpen * roomsSold PL!row14
//   FBPayroll      = annual * costRamp * inflIdx * assetOpen              PL!row16
//   COGS           = pct * costRamp * FBRev (нет inflIdx)                PL!row17
//   MECosts        = pct * costRamp * OtherRev                           PL!row19
//   WellnessPayroll= annual * costRamp * inflIdx * assetOpen             PL!row21
//   WellnessOther  = fixed * 12 * assetOpen * inflIdx * costRamp         PL!row22
//   OtherDirect    = fixed * 12 * assetOpen * inflIdx * costRamp         PL!row24
//   AGPayroll      = annual * costRamp * inflIdx * assetOpen             PL!row31
//   OtherAG        = pct * costRamp * TotalRev                           PL!row32
//   TechSubs       = fixed * 12 * assetOpen * inflIdx * costRamp         PL!row34
//   SMPayroll      = annual * costRamp * inflIdx * assetOpen             PL!row36
//   OtherSM        = pct * costRamp * TotalRev                           PL!row37
//   MaintPayroll   = annual * costRamp * inflIdx * assetOpen             PL!row39
//   MaintOther     = pct * costRamp * TotalRev                           PL!row40
//   Utilities      = pct * costRamp * TotalRev                           PL!row42
//   MgmtFee        = pct * costRamp * TotalRev                           PL!row48
//   PropTaxes      = pct * costRamp * TotalRev                           PL!row49
//   Insurance      = pct * costRamp * TotalRev                           PL!row50
//   Rent           = fixed * 12 * assetOpen * inflIdx  (НЕТ costRamp!)  PL!row56
//
// EBITDA = TotalRev + всеCosts + Rent                                    PL!row57
// Depreciation = -depPct * grandTotal * assetOpen                        PL!row60
// ─────────────────────────────────────────────────────────────────────────────

interface PLByYear {
  roomsRevenue: number[];
  fbRevenue: number[];
  spaRevenue: number[];
  otherRevenue: number[];
  totalRevenue: number[];
  ebitda: number[]; // индекс 0 = Year1 .. 9 = Year10
  depreciation: number[];
}

export function computePLByYear(deal: Deal): PLByYear {
  const kpis = deal.kpis!;
  const ramp = kpis.rampSettings!;
  const opex = kpis.opex!;
  const fnb = kpis.fnb!;
  const oRev = kpis.otherRev!;

  const exitYear = resolveExitYear(deal) ?? 10;

  // ── macro ──────────────────────────────────────────────────────────────────
  const toplineGrowthPct = (ramp.toplineGrowthPct ?? 3) / 100;
  const inflationPct = (ramp.inflationPct ?? 5) / 100;
  const depreciationPct = (ramp.depreciationPctOfCapex ?? 3) / 100;

  const revRampArr = ramp.revenueRamp || [0.85, 1, 1, 1];
  const costRampArr = ramp.costRamp || [1.15, 1.1, 1, 1];

  const revRamp = (n: number) =>
    revRampArr[Math.min(n - 1, revRampArr.length - 1)];
  const costRamp = (n: number) =>
    costRampArr[Math.min(n - 1, costRampArr.length - 1)];
  const topIdx = (n: number) => Math.pow(1 + toplineGrowthPct, n);
  const inflIdx = (n: number) => Math.pow(1 + inflationPct, n);
  const open = (n: number) => (n > 0 && n <= exitYear ? 1 : 0);

  // ── room base assumptions ──────────────────────────────────────────────────
  const rt = kpis.roomRevenue?.totals || kpis.roomRev;
  const baseADR = rt?.avgADR ?? kpis.avgADR ?? 100;
  const baseOcc = (rt?.avgOccPct ?? kpis.avgOccupancy ?? 62) / 100;
  const totalRooms = kpis.roomsTotal ?? kpis.rooms?.total ?? 30;

  // ── payroll по отделам ────────────────────────────────────────────────────
  //
  // PayrollDataRecord структура (таблица property_payroll_data):
  //   { id, data: { positions: [...], socialChargesPercent: 34.8 }, ... }
  //
  // Positions: { departmentId, monthlySalary, calculatedFTEs, included }
  //
  // Department ID → категория payroll:
  //   Rooms    = '0d04c044' (Front Office) + 'e156800b' (Housekeeping)
  //   F&B      = 'd93b774f'
  //   Wellness = '559c7da5'
  //   A&G      = '508d5c3b'
  //   S&M/HR   = '8607eb3b'
  //   Maint    = 'c5e5738e'
  //
  // Расчёт: monthlySalary * 12 * calculatedFTEs * (1 + socialChargesPercent/100)
  // ────────────────────────────────────────────────────────────────────────────
  const totalPayroll = kpis.payroll?.totalAnnual ?? 0;

  // payrollData передаётся через deal.payrollData (прокидывается из buildCashFlowStatement)
  const payrollRecord = (deal as any).payrollData;
  const positions: Array<{
    departmentId: string;
    monthlySalary: number;
    calculatedFTEs: number;
    included: boolean;
  }> = payrollRecord?.data?.positions ?? [];

  const socialRate = (payrollRecord?.data?.socialChargesPercent ?? 34.8) / 100;

  // Маппинг departmentId → группа
  const DEPT_ROOMS = new Set([
    "0d04c044-87bd-4d24-9007-f0628ef6ee3a",
    "e156800b-55e8-4b3f-a5f9-64b8fe266512",
  ]);
  const DEPT_FB = new Set(["d93b774f-3906-47e9-ba2f-90675588f31c"]);
  const DEPT_WELLNESS = new Set(["559c7da5-01f0-4a2d-9764-ad7d6fb70220"]);
  const DEPT_AG = new Set(["508d5c3b-c493-43f9-a796-11558a7a1fc9"]);
  const DEPT_SM = new Set(["8607eb3b-2103-4615-b96c-9eef9f282afa"]);
  const DEPT_MAINT = new Set(["c5e5738e-0b31-4940-9b10-8ca654b9917f"]);

  function sumByDeptIds(deptSet: Set<string>): number {
    return positions
      .filter((p) => p.included && deptSet.has(p.departmentId))
      .reduce(
        (s, p) =>
          s + p.monthlySalary * 12 * p.calculatedFTEs * (1 + socialRate),
        0,
      );
  }

  let pRooms = sumByDeptIds(DEPT_ROOMS);
  let pFB = sumByDeptIds(DEPT_FB);
  let pWellness = sumByDeptIds(DEPT_WELLNESS);
  let pAG = sumByDeptIds(DEPT_AG);
  let pSM = sumByDeptIds(DEPT_SM);
  let pMaint = sumByDeptIds(DEPT_MAINT);

  // Fallback: если positions пустые — используем веса от totalAnnual
  if (pRooms + pFB + pWellness + pAG + pSM + pMaint === 0) {
    pRooms = totalPayroll * 0.236;
    pFB = totalPayroll * 0.346;
    pWellness = totalPayroll * 0.11;
    pAG = totalPayroll * 0.152;
    pSM = totalPayroll * 0.073;
    pMaint = totalPayroll * 0.083;
  }

  // ── opex ──────────────────────────────────────────────────────────────────
  const commPct = (opex.roomsCommission ?? 15) / 100;
  const guestSuppliesPN = opex.guestSuppliesCleaning ?? 8;
  const cogsPct = (opex.cogs ?? 30) / 100;
  const mePct = (opex.meetingsEvents ?? 2) / 100;
  const wellnessOtherFix = opex.wellnessOther ?? 0;
  const otherDirectFix = opex.otherDirect ?? 0;
  const agPct = (opex.adminGeneralOther ?? 4) / 100;
  const techFix = opex.techSubscriptions ?? 0;
  const smPct = (opex.salesMarketingOther ?? 3) / 100;
  const maintPct = (opex.maintenanceOther ?? 2) / 100;
  const utilPct = (opex.utilities ?? 3) / 100;
  const mgmtPct = (opex.mgmtFee ?? 3) / 100;
  const propTaxPct = (opex.propertyTaxes ?? 1) / 100;
  const insPct = (opex.insurance ?? 0.5) / 100;
  const rentFix = opex.rent ?? 0;

  // ── F&B ────────────────────────────────────────────────────────────────────
  const avgGuests = fnb.avgGuests ?? 2.8;
  const capBreakfast = (fnb.capture?.breakfast ?? 65) / 100;
  const capLunch = (fnb.capture?.lunch ?? 20) / 100;
  const capDinner = (fnb.capture?.dinner ?? 15) / 100;
  const capBar = (fnb.capture?.bar ?? 30) / 100;
  const ckGBreakfast = fnb.check?.guest?.breakfast ?? 54;
  const ckGLunch = fnb.check?.guest?.lunch ?? 22;
  const ckGDinner = fnb.check?.guest?.dinner ?? 28;
  const ckGBar = fnb.check?.guest?.bar ?? 8;
  const extDBreakfast = fnb.external?.perDay?.breakfast ?? 5;
  const extDLunch = fnb.external?.perDay?.lunch ?? 10;
  const extDDinner = fnb.external?.perDay?.dinner ?? 20;
  const extDBar = fnb.external?.perDay?.bar ?? 25;
  const ckEBreakfast = fnb.check?.external?.breakfast ?? 50;
  const ckELunch = fnb.check?.external?.lunch ?? 20;
  const ckEDinner = fnb.check?.external?.dinner ?? 69;
  const ckEBar = fnb.check?.external?.bar ?? 18;

  // ── Spa & Other ────────────────────────────────────────────────────────────
  const treatmentsPerDay = oRev.wellness?.treatmentsPerDay ?? 4;
  const avgPriceTreatment = oRev.wellness?.avgPrice ?? 1200;
  const otherPctRooms =
    oRev.other?.mode === "pct" ? (oRev.other.value ?? 0) / 100 : 0;
  const otherFixMonthly =
    oRev.other?.mode === "fixed" ? (oRev.other.value ?? 0) : 0;

  // ── project cost (для амортизации) ────────────────────────────────────────
  const projectCost =
    kpis.investmentBudget?.grandTotal ||
    kpis.budget?.grandTotal ||
    (deal as any).budget?.grandTotal ||
    0;

  // ── per-year loop ──────────────────────────────────────────────────────────
  const roomsRevArr: number[] = [];
  const fbRevArr: number[] = [];
  const spaRevArr: number[] = [];
  const otherRevArr: number[] = [];
  const totalRevArr: number[] = [];
  const ebitdaArr: number[] = [];
  const depArr: number[] = [];

  for (let n = 1; n <= 10; n++) {
    const o = open(n);
    const ti = topIdx(n);
    const ii = inflIdx(n);
    const rr = revRamp(n);
    const cr = costRamp(n);

    // Revenue
    const adr = baseADR * ti * rr * o;
    const occ = baseOcc * rr * o;
    const roomsAvail = totalRooms * 365 * o;
    const roomsSold = occ * roomsAvail;
    const roomsRev = adr * roomsSold;

    const guests = avgGuests * roomsSold;

    // ── F&B Revenue — формула Excel (КРИТИЧНО!) ──────────────────────────────
    // Excel применяет revRamp (rr) И toplineIndex (ti) к каждому множителю ОТДЕЛЬНО:
    //   G87: capture[n]    = baseCapture * rr * o
    //   G88: guestCheck[n] = baseCheck   * rr * ti * o
    //   G89: extPerDay[n]  = baseExt     * rr * o
    //   G90: extCheck[n]   = baseExtCheck* rr * ti * o
    //   G91: revenue = TotalGuests * capture[n] * check[n] + extPerDay[n]*365*extCheck[n]
    // rr применяется ДВАЖДЫ (через roomsSold и через capture/check), ti — к чекам.
    const brkCapture = capBreakfast * rr * o;
    const brkCheck = ckGBreakfast * rr * ti * o;
    const brkExtDay = extDBreakfast * rr * o;
    const brkExtCheck = ckEBreakfast * rr * ti * o;
    const breakfastRev =
      guests * brkCapture * brkCheck + brkExtDay * 365 * brkExtCheck;

    const lnCapture = capLunch * rr * o;
    const lnCheck = ckGLunch * rr * ti * o;
    const lnExtDay = extDLunch * rr * o;
    const lnExtCheck = ckELunch * rr * ti * o;
    const lunchRev = guests * lnCapture * lnCheck + lnExtDay * 365 * lnExtCheck;

    const dnCapture = capDinner * rr * o;
    const dnCheck = ckGDinner * rr * ti * o;
    const dnExtDay = extDDinner * rr * o;
    const dnExtCheck = ckEDinner * rr * ti * o;
    const dinnerRev =
      guests * dnCapture * dnCheck + dnExtDay * 365 * dnExtCheck;

    const brCapture = capBar * rr * o;
    const brCheck = ckGBar * rr * ti * o;
    const brExtDay = extDBar * rr * o;
    const brExtCheck = ckEBar * rr * ti * o;
    const barRev = guests * brCapture * brCheck + brExtDay * 365 * brExtCheck;

    const fbRev = breakfastRev + lunchRev + dinnerRev + barRev;

    // ── Spa Revenue — формула Excel (КРИТИЧНО!) ───────────────────────────────
    // G111: treatments[n] = baseTreatments * rr * o
    // G112: avgPrice[n]   = basePrice * rr * ti * o
    // G113: spaRev = treatments[n] * 365 * avgPrice[n]  → rr^2 * ti в итоге
    const treatmentsN = treatmentsPerDay * rr * o;
    const priceN = avgPriceTreatment * rr * ti * o;
    const spaRev = treatmentsN * 365 * priceN;

    // ── Other Revenue — формула Excel ─────────────────────────────────────────
    // G115: pct[n]          = basePct   * rr * o
    // G116: fixedMonthly[n] = baseFixed * rr * ti * o
    // G117: = G115*roomsRev + G116*12
    const pctN = otherPctRooms * rr * o;
    const fixedMonthlyN = otherFixMonthly * rr * ti * o;
    const otherRev_ = pctN * roomsRev + fixedMonthlyN * 12;

    const totalRev = roomsRev + fbRev + spaRev + otherRev_;

    // Costs (все отрицательные)
    // Direct
    const roomsPayCost = -pRooms * cr * ii * o;
    const commission = -commPct * cr * roomsRev; // нет inflIdx!
    const guestSuppl = -guestSuppliesPN * cr * ii * o * roomsSold;
    const fbPayCost = -pFB * cr * ii * o;
    const cogs = -cogsPct * cr * fbRev; // нет inflIdx!
    const meCost = -mePct * cr * otherRev_; // M&E % от other
    const wellPayCost = -pWellness * cr * ii * o;
    const wellOtherCost = -wellnessOtherFix * 12 * o * ii * cr;
    const otherDirCost = -otherDirectFix * 12 * o * ii * cr;

    // Indirect
    const agPayCost = -pAG * cr * ii * o;
    const otherAG = -agPct * cr * totalRev;
    const techCost = -techFix * 12 * o * ii * cr;
    const smPayCost = -pSM * cr * ii * o;
    const otherSM = -smPct * cr * totalRev;
    const maintPayCost = -pMaint * cr * ii * o;
    const maintOther = -maintPct * cr * totalRev;
    const utilities = -utilPct * cr * totalRev;

    // Other fixed costs
    const mgmtFee = -mgmtPct * cr * totalRev;
    const propTax = -propTaxPct * cr * totalRev;
    const insurance = -insPct * cr * totalRev;

    // Rent: НЕТ costRamp — Excel: =-D56*12*G66*G77  (нет G80!)
    const rent = -rentFix * 12 * o * ii;

    const ebitda =
      totalRev +
      roomsPayCost +
      commission +
      guestSuppl +
      fbPayCost +
      cogs +
      meCost +
      wellPayCost +
      wellOtherCost +
      otherDirCost +
      agPayCost +
      otherAG +
      techCost +
      smPayCost +
      otherSM +
      maintPayCost +
      maintOther +
      utilities +
      mgmtFee +
      propTax +
      insurance +
      rent;

    // Depreciation = -depPct * grandTotal * assetOpen
    const depreciation = -depreciationPct * projectCost * o;

    roomsRevArr.push(roomsRev);
    fbRevArr.push(fbRev);
    spaRevArr.push(spaRev);
    otherRevArr.push(otherRev_);
    totalRevArr.push(totalRev);
    ebitdaArr.push(ebitda);
    depArr.push(depreciation);
  }

  return {
    roomsRevenue: roomsRevArr,
    fbRevenue: fbRevArr,
    spaRevenue: spaRevArr,
    otherRevenue: otherRevArr,
    totalRevenue: totalRevArr,
    ebitda: ebitdaArr,
    depreciation: depArr,
  };
}

// ─── DEBT SCHEDULE ────────────────────────────────────────────────────────────
//
// Excel: Cashflow sheet, строки 22-28
//
// DebtBoP[n]  = DebtEoP[n-1]
// Draw[0]     = loanAmount
// Interest[n] = IF(n<=exitYear, (BoP+Draw)*rate, 0)
// RegPrin[n]  = loanAmount/amortYears * assetOpen   (фиксированная сумма)
// Balloon[n]  = IF(exitFlag, max(0, BoP-RegPrin), 0)
// EoP[n]      = BoP + Draw - RegPrin - Balloon
// ─────────────────────────────────────────────────────────────────────────────
export function computeDebtSchedule(deal: Deal): DebtSchedule {
  const years = getStandardYears();
  const totalYears = years.length;
  const fin = deal.kpis?.financing;

  const ltcPct = fin?.ltcPct || fin?.ltv || 0;
  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    (deal as any).budget?.grandTotal ||
    0;
  const exitYear = resolveExitYear(deal);

  if (!fin || projectCost === 0 || ltcPct === 0) {
    const zero = years.map((_, i) => ({ year: i, value: 0 }));
    return {
      rows: [
        { id: "debt-bop", label: "Debt BoP", type: "debt", years: zero },
        {
          id: "debt-drawdown",
          label: "Debt Drawdown",
          type: "debt",
          years: zero,
        },
        { id: "interest", label: "Interest", type: "debt", years: zero },
        { id: "repayment", label: "Repayment", type: "debt", years: zero },
        {
          id: "balloon-repayment",
          label: "Balloon Repayment",
          type: "debt",
          years: zero,
        },
        { id: "debt-eop", label: "Debt EoP", type: "balance", years: zero },
      ],
      totalLoanAmount: 0,
      totalInterest: 0,
      totalPrincipalRepaid: 0,
    };
  }

  const loanAmount = projectCost * (ltcPct / 100);
  const interestRate = (fin.interestRatePct || fin.interestRate || 5) / 100;
  const amortYears = fin.amortYears || fin.amortizationYears || 25;
  const loanTermYears = fin.loanTermYears || amortYears;
  const annualPrin = loanAmount / amortYears;

  const bop = new Array(totalYears).fill(0);
  const draw = new Array(totalYears).fill(0);
  const interest = new Array(totalYears).fill(0);
  const repay = new Array(totalYears).fill(0);
  const balloon = new Array(totalYears).fill(0);
  const eop = new Array(totalYears).fill(0);

  draw[0] = loanAmount;
  eop[0] = loanAmount;

  for (let i = 1; i < totalYears; i++) {
    bop[i] = eop[i - 1];

    const isExit = exitYear !== null && i === exitYear;
    const isOpen = i <= (exitYear ?? loanTermYears);
    const inTerm = i <= loanTermYears;

    interest[i] = isOpen && inTerm ? (bop[i] + draw[i]) * interestRate : 0;
    repay[i] = isOpen && inTerm ? annualPrin : 0;
    balloon[i] = isExit ? Math.max(0, bop[i] - repay[i]) : 0;
    eop[i] = Math.max(0, bop[i] + draw[i] - repay[i] - balloon[i]);
  }

  const rows: DebtScheduleRow[] = [
    {
      id: "debt-bop",
      label: "Debt BoP",
      type: "debt",
      years: years.map((_, i) => ({ year: i, value: bop[i] })),
    },
    {
      id: "debt-drawdown",
      label: "Debt Drawdown",
      type: "debt",
      years: years.map((_, i) => ({ year: i, value: draw[i] })),
    },
    {
      id: "interest",
      label: "Interest",
      type: "debt",
      years: years.map((_, i) => ({ year: i, value: -interest[i] })),
    },
    {
      id: "repayment",
      label: "Repayment",
      type: "debt",
      years: years.map((_, i) => ({ year: i, value: -repay[i] })),
    },
    {
      id: "balloon-repayment",
      label: "Balloon Repayment",
      type: "debt",
      years: years.map((_, i) => ({ year: i, value: -balloon[i] })),
    },
    {
      id: "debt-eop",
      label: "Debt EoP",
      type: "balance",
      years: years.map((_, i) => ({ year: i, value: eop[i] })),
    },
  ];

  const totalInterest = interest.reduce((s, v) => s + v, 0);
  const totalPrinRepaid =
    repay.reduce((s, v) => s + v, 0) + balloon.reduce((s, v) => s + v, 0);

  return {
    rows,
    totalLoanAmount: loanAmount,
    totalInterest,
    totalPrincipalRepaid: totalPrinRepaid,
  };
}

export function transformDebtScheduleToRowData(
  ds: DebtSchedule,
): DebtScheduleRowData[] {
  const bopRow = ds.rows.find((r) => r.id === "debt-bop");
  const repRow = ds.rows.find((r) => r.id === "repayment");
  const balRow = ds.rows.find((r) => r.id === "balloon-repayment");
  const intRow = ds.rows.find((r) => r.id === "interest");
  const eopRow = ds.rows.find((r) => r.id === "debt-eop");

  return Array.from({ length: 10 }, (_, i) => {
    const year = i + 1;
    const b = bopRow?.years.find((y) => y.year === year)?.value || 0;
    const int_ = Math.abs(
      intRow?.years.find((y) => y.year === year)?.value || 0,
    );
    const reg = Math.abs(
      repRow?.years.find((y) => y.year === year)?.value || 0,
    );
    const bal = Math.abs(
      balRow?.years.find((y) => y.year === year)?.value || 0,
    );
    const e = eopRow?.years.find((y) => y.year === year)?.value || 0;
    return {
      year,
      beginningBalance: b,
      payment: int_ + reg + bal,
      interest: -int_,
      principal: -(reg + bal),
      endingBalance: e,
    };
  });
}

// ─── UNLEVERED CASH FLOW ─────────────────────────────────────────────────────
//
// Excel: Cashflow sheet, строки 5-10
//
// CashTaxes = MIN(-taxRate * NetIncome, 0)
//   NetIncome = EBITDA + Depreciation + InterestExpense  (PL row62)
//
// GrossDisposal = IF(exitFlag, EBITDA*(1+topline-inflation)/capRate, 0)
//
// UnleveredCF = EBITDA + CashTaxes + CapEx + GrossDisposal + SellingCosts
//   (долг НЕ вычитается из unlevered — только в levered)
// ─────────────────────────────────────────────────────────────────────────────
export function computeUnleveredCashflowByYear(deal: Deal): {
  years: YearKey[];
  ebitda: SeriesByYear;
  cashTaxes: SeriesByYear;
  capex: SeriesByYear;
  grossDisposalProceeds: SeriesByYear;
  sellingCosts: SeriesByYear;
  netSaleProceeds: SeriesByYear;
  unleveredCF: SeriesByYear;
  totalRevenue: SeriesByYear;
} {
  const years = getStandardYears();
  const pl = computePLByYear(deal);
  const debtSchedule = computeDebtSchedule(deal);
  const interestRow = debtSchedule.rows.find((r) => r.id === "interest");

  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    (deal as any).budget?.grandTotal ||
    0;

  const taxRate = (deal.kpis?.financing?.taxRateOnEBT || 25) / 100;
  const exitSettings = deal.kpis?.exit;
  const exitYear = resolveExitYear(deal);
  const toplineGrowth = (deal.kpis?.rampSettings?.toplineGrowthPct ?? 3) / 100;
  const inflation = (deal.kpis?.rampSettings?.inflationPct ?? 5) / 100;

  const ebitda: SeriesByYear = {};
  const cashTaxes: SeriesByYear = {};
  const capex: SeriesByYear = {};
  const grossDisposalProceeds: SeriesByYear = {};
  const sellingCosts: SeriesByYear = {};
  const netSaleProceeds: SeriesByYear = {};
  const unleveredCF: SeriesByYear = {};
  const totalRevenue: SeriesByYear = {};

  years.forEach((y) => {
    const n = Number(y.slice(1));

    ebitda[y] = n === 0 ? 0 : pl.ebitda[n - 1] || 0;
    totalRevenue[y] = n === 0 ? 0 : pl.totalRevenue[n - 1] || 0;
    capex[y] = n === 0 ? -projectCost : 0;
    grossDisposalProceeds[y] = 0;
    sellingCosts[y] = 0;
    netSaleProceeds[y] = 0;

    if (n === 0) {
      cashTaxes[y] = 0;
    } else {
      // Cash Taxes = MIN(-taxRate * NetIncome, 0)
      // NetIncome = EBITDA + Depreciation(отриц) + Interest(отриц)
      const dep = pl.depreciation[n - 1] || 0;
      const intExp = interestRow?.years.find((yd) => yd.year === n)?.value || 0;
      const netIncome = (ebitda[y] || 0) + dep + intExp;
      cashTaxes[y] = Math.min(-taxRate * netIncome, 0);
    }
  });

  // Gross Disposal в год выхода
  if (exitYear) {
    const exitKey = `y${exitYear}` as YearKey;
    if (years.includes(exitKey)) {
      const noiAtExit = ebitda[exitKey] || 0;
      const method =
        exitSettings?.sale?.valuationMethod ||
        exitSettings?.valuationMethod ||
        "capRate";

      // Forward NOI = EBITDA * (1 + toplineGrowth - inflation)
      const forwardNOI = noiAtExit * (1 + toplineGrowth - inflation);

      let grossPrice = 0;
      if (method === "capRate") {
        const capRate =
          (exitSettings?.sale?.exitCapRate || exitSettings?.exitCapRate || 7) /
          100;
        grossPrice = capRate > 0 && forwardNOI > 0 ? forwardNOI / capRate : 0;
      } else {
        const multiple =
          exitSettings?.sale?.exitNOIMultiple ||
          exitSettings?.exitNOIMultiple ||
          12;
        grossPrice = forwardNOI * multiple;
      }

      const sellCostsPct =
        (exitSettings?.sale?.sellingCostsPct ||
          exitSettings?.sellingCostsPct ||
          2) / 100;
      grossDisposalProceeds[exitKey] = grossPrice;
      sellingCosts[exitKey] = -grossPrice * sellCostsPct;
      netSaleProceeds[exitKey] = grossPrice * (1 - sellCostsPct);
    }
  }

  years.forEach((y) => {
    unleveredCF[y] =
      (ebitda[y] || 0) +
      (cashTaxes[y] || 0) +
      (capex[y] || 0) +
      (grossDisposalProceeds[y] || 0) +
      (sellingCosts[y] || 0);
  });

  return {
    years,
    ebitda,
    cashTaxes,
    capex,
    grossDisposalProceeds,
    sellingCosts,
    netSaleProceeds,
    unleveredCF,
    totalRevenue,
  };
}

// ─── LEVERED CASH FLOW ───────────────────────────────────────────────────────
export function computeLeveredCashflowByYear(deal: Deal): {
  debtDraw: SeriesByYear;
  interestExpense: SeriesByYear;
  principalRepayment: SeriesByYear;
  balloonRepayment: SeriesByYear;
  leveredCF: SeriesByYear;
} {
  const years = getStandardYears();
  const ds = computeDebtSchedule(deal);

  const drawRow = ds.rows.find((r) => r.id === "debt-drawdown");
  const intRow = ds.rows.find((r) => r.id === "interest");
  const repRow = ds.rows.find((r) => r.id === "repayment");
  const balRow = ds.rows.find((r) => r.id === "balloon-repayment");

  const { ebitda, cashTaxes, capex, grossDisposalProceeds, sellingCosts } =
    computeUnleveredCashflowByYear(deal);

  const debtDraw: SeriesByYear = {};
  const interestExpense: SeriesByYear = {};
  const principalRepayment: SeriesByYear = {};
  const balloonRepayment: SeriesByYear = {};
  const leveredCF: SeriesByYear = {};

  years.forEach((y) => {
    const n = Number(y.slice(1));
    debtDraw[y] = drawRow?.years.find((yd) => yd.year === n)?.value || 0;
    interestExpense[y] = intRow?.years.find((yd) => yd.year === n)?.value || 0;
    principalRepayment[y] =
      repRow?.years.find((yd) => yd.year === n)?.value || 0;
    balloonRepayment[y] = balRow?.years.find((yd) => yd.year === n)?.value || 0;

    const ucf =
      (ebitda[y] || 0) +
      (cashTaxes[y] || 0) +
      (capex[y] || 0) +
      (grossDisposalProceeds[y] || 0) +
      (sellingCosts[y] || 0);

    leveredCF[y] =
      ucf +
      debtDraw[y] +
      interestExpense[y] +
      principalRepayment[y] +
      balloonRepayment[y];
  });

  return {
    debtDraw,
    interestExpense,
    principalRepayment,
    balloonRepayment,
    leveredCF,
  };
}

// ─── IRR ─────────────────────────────────────────────────────────────────────
export function computeProjectIrrs(deal: Deal): {
  unleveredIrr: number | null;
  leveredIrr: number | null;
} {
  const uData = computeUnleveredCashflowByYear(deal);
  const lData = computeLeveredCashflowByYear(deal);

  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    (deal as any).budget?.grandTotal ||
    0;
  const ltcPct = deal.kpis?.financing?.ltcPct || 0;
  const equity = projectCost - projectCost * (ltcPct / 100);
  const exitYear = resolveExitYear(deal) ?? 10;

  const uFlows: number[] = [-projectCost];
  const lFlows: number[] = [-equity];

  for (let i = 1; i <= exitYear; i++) {
    const k = `y${i}` as YearKey;
    uFlows.push(uData.unleveredCF[k] || 0);
    lFlows.push(lData.leveredCF[k] || 0);
  }

  return { unleveredIrr: safeIrr(uFlows), leveredIrr: safeIrr(lFlows) };
}

export function computeProjectIrrsWithHorizon(
  deal: Deal,
  horizon: number,
  options?: {
    strategy?: ExitStrategy;
    unleveredTerminalValue?: number;
    leveredTerminalValue?: number;
  },
): { unleveredIrr: number | null; leveredIrr: number | null } {
  const uData = computeUnleveredCashflowByYear(deal);
  const lData = computeLeveredCashflowByYear(deal);

  const projectCost =
    deal.kpis?.investmentBudget?.grandTotal ||
    deal.kpis?.budget?.grandTotal ||
    (deal as any).budget?.grandTotal ||
    0;
  const ltcPct = deal.kpis?.financing?.ltcPct || 0;
  const equity = projectCost - projectCost * (ltcPct / 100);

  const uFlows: number[] = [-projectCost];
  const lFlows: number[] = [-equity];

  for (let i = 1; i <= horizon; i++) {
    const k = `y${i}` as YearKey;
    uFlows.push(uData.unleveredCF[k] || 0);
    lFlows.push(lData.leveredCF[k] || 0);
  }

  if (options?.strategy !== "HOLD_FOREVER") {
    if (options?.unleveredTerminalValue !== undefined)
      uFlows.push(options.unleveredTerminalValue);
    if (options?.leveredTerminalValue !== undefined)
      lFlows.push(options.leveredTerminalValue);
  }

  return { unleveredIrr: safeIrr(uFlows), leveredIrr: safeIrr(lFlows) };
}

// ─── FULL STATEMENT ──────────────────────────────────────────────────────────
export function buildCashFlowStatement(
  deal: Deal,
  payrollData?: unknown,
): CashFlowResult {
  const emptyResult: CashFlowResult = {
    rows: [],
    debtScheduleRows: [],
    kpis: {
      unlevered10y: 0,
      levered10y: 0,
      tax10y: 0,
      avgEbitdaMargin: 0,
      unleveredIRR: null,
      leveredIRR: null,
    },
    exitYear: null,
    debtSchedule: {
      rows: [],
      totalLoanAmount: 0,
      totalInterest: 0,
      totalPrincipalRepaid: 0,
    },
  };
  if (!deal) return emptyResult;

  // Прокидываем payrollData в deal чтобы computePLByYear мог его использовать
  const dealWithPayroll = payrollData
    ? ({ ...deal, payrollData } as Deal)
    : deal;

  const ds = computeDebtSchedule(dealWithPayroll);
  const dtd = transformDebtScheduleToRowData(ds);

  const {
    years: yks,
    ebitda,
    cashTaxes,
    capex,
    grossDisposalProceeds,
    sellingCosts,
    netSaleProceeds,
    unleveredCF,
    totalRevenue,
  } = computeUnleveredCashflowByYear(dealWithPayroll);

  const {
    debtDraw,
    interestExpense,
    principalRepayment,
    balloonRepayment,
    leveredCF,
  } = computeLeveredCashflowByYear(dealWithPayroll);

  const unlevered10y = Object.values(unleveredCF).reduce((a, b) => a + b, 0);
  const levered10y = Object.values(leveredCF).reduce((a, b) => a + b, 0);
  const tax10y = Math.abs(Object.values(cashTaxes).reduce((a, b) => a + b, 0));
  const { unleveredIrr, leveredIrr } = computeProjectIrrs(dealWithPayroll);

  let totRev = 0,
    totEbitda = 0;
  yks.forEach((k) => {
    if (k !== "y0") {
      totRev += totalRevenue[k] || 0;
      totEbitda += ebitda[k] || 0;
    }
  });
  const avgEbitdaMargin = totRev > 0 ? totEbitda / totRev : 0;

  const fmt = (d: SeriesByYear) =>
    yks.map((k) => ({ year: Number(k.slice(1)), value: d[k] || 0 }));

  const rows: CashFlowRow[] = [
    {
      id: "memo-section",
      label: "INCOME STATEMENT (Memo)",
      section: "memo",
      type: "section",
      years: fmt({}),
    },
    {
      id: "total-revenue",
      label: "Total Revenue",
      section: "memo",
      type: "line",
      years: fmt(totalRevenue),
    },
    {
      id: "ebitda",
      label: "EBITDA / NOI",
      section: "memo",
      type: "line",
      years: fmt(ebitda),
    },
    {
      id: "unlevered-section",
      label: "UNLEVERED CASH FLOW",
      section: "unlevered",
      type: "section",
      years: fmt({}),
    },
    {
      id: "ebitda-ucf",
      label: "EBITDA / NOI",
      section: "unlevered",
      type: "line",
      years: fmt(ebitda),
    },
    {
      id: "cash-taxes",
      label: "Cash Taxes",
      section: "unlevered",
      type: "line",
      years: fmt(cashTaxes),
    },
    {
      id: "capex",
      label: "CapEx (Investment)",
      section: "unlevered",
      type: "line",
      years: fmt(capex),
    },
    {
      id: "gross-disposal-proceeds",
      label: "Gross Disposal Proceeds",
      section: "unlevered",
      type: "line",
      years: fmt(grossDisposalProceeds),
    },
    {
      id: "selling-costs",
      label: "Selling Costs",
      section: "unlevered",
      type: "line",
      years: fmt(sellingCosts),
    },
    {
      id: "net-sale-proceeds",
      label: "Net Sale Proceeds",
      section: "unlevered",
      type: "line",
      years: fmt(netSaleProceeds),
    },
    {
      id: "unlevered-cf",
      label: "Unlevered Cash Flow",
      section: "unlevered",
      type: "total",
      years: fmt(unleveredCF),
    },
    {
      id: "levered-section",
      label: "LEVERED CASH FLOW",
      section: "levered",
      type: "section",
      years: fmt({}),
    },
    {
      id: "ucf-lcf",
      label: "Unlevered Cash Flow",
      section: "levered",
      type: "line",
      years: fmt(unleveredCF),
    },
    {
      id: "debt-draw",
      label: "Debt Draw",
      section: "levered",
      type: "line",
      years: fmt(debtDraw),
    },
    {
      id: "interest-expense",
      label: "Interest Expense",
      section: "levered",
      type: "line",
      years: fmt(interestExpense),
    },
    {
      id: "principal-repayment",
      label: "Principal Repayment",
      section: "levered",
      type: "line",
      years: fmt(principalRepayment),
    },
    {
      id: "balloon-repayment",
      label: "Balloon Repayment",
      section: "levered",
      type: "line",
      years: fmt(balloonRepayment),
    },
    {
      id: "levered-cf",
      label: "Levered Cash Flow",
      section: "levered",
      type: "total",
      years: fmt(leveredCF),
    },
  ];

  return {
    rows,
    debtScheduleRows: dtd,
    kpis: {
      unlevered10y,
      levered10y,
      tax10y,
      avgEbitdaMargin,
      unleveredIRR: unleveredIrr,
      leveredIRR: leveredIrr,
      totalLoanAmount: ds.totalLoanAmount,
      totalInterest: Math.abs(ds.totalInterest),
    },
    exitYear: resolveExitYear(deal),
    debtSchedule: ds,
  };
}
